import { useMemo } from "react";
import { ChevronDown, Info } from "lucide-react";
import { formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Collapsible, CollapsibleContent, CollapsibleTrigger, Shimmer, WorkSteps, type WorkStepLike } from "@ncp/ui";
import type { LiveTurn } from "../hooks";
import { readableDetail, useToolLabel } from "./labels";

export interface TraceCall { id: string; tool: string; agentKey?: string | null; phase: string; summary?: string | null; durationMs?: number | null }

/** The work behind an answer as one folded line ("Worked through 4 steps · 6 s"), each step named in plain words. */
export function TurnSteps({ calls, running = false, runningLabel, specialistName }: {
  calls: TraceCall[];
  running?: boolean;
  /** The line shown while no step is running yet ("Thinking…", "Verifying citations…"). */
  runningLabel?: string;
  specialistName: (key: string) => string;
}) {
  const { t } = useTranslation();
  const toolLabel = useToolLabel();
  const steps = useMemo<WorkStepLike[]>(() => calls.map((c) => {
    const status: WorkStepLike["status"] = c.phase === "error" ? "error" : c.phase === "start" ? "running" : "done";
    const label = c.tool === "delegate_to_agent" && c.agentKey ? t("ai.chat.askedSpecialist", { agent: specialistName(c.agentKey) }) : toolLabel(c.tool, status === "running");
    return { id: c.id, label, detail: readableDetail(c.summary), status, durationMs: c.durationMs };
  }), [calls, t, toolLabel, specialistName]);
  return (
    <WorkSteps
      steps={steps}
      running={running}
      label={running && !steps.some((s) => s.status === "running") ? runningLabel : undefined}
      summary={steps.length > 0 ? t("ai.chat.workedSteps", { count: steps.length }) : undefined}
    />
  );
}

/** The steps of the turn being answered: the current step shimmers until the answer starts to arrive, then the line folds. */
export function LiveSteps({ live, streaming, specialistName }: { live: LiveTurn; streaming: boolean; specialistName: (key: string) => string }) {
  const { t } = useTranslation();
  const calls = useMemo<TraceCall[]>(() => live.toolCalls.map((c) => ({ id: c.id, tool: c.tool, agentKey: c.agentKey, phase: c.phase, summary: c.summary, durationMs: c.durationMs })), [live.toolCalls]);
  const working = streaming && !live.content && !live.error;
  const stage = live.stage ? t(`ai.chat.stage.${live.stage}`, { defaultValue: t("ai.chat.stage.thinking") }) : t("ai.chat.stage.thinking");
  return <TurnSteps calls={calls} running={working} runningLabel={stage} specialistName={specialistName} />;
}

interface ProgressStep { label?: string; labelAr?: string | null; status?: string }
interface ProgressMember { status?: string }

/**
 * The live steps of an answer that is prose with inline widgets: one quiet line where its first line of text will be, until that text
 * arrives — the plan's running step (or the tool at work, or the stage) and how far the plan or the team has come ("2 of 3 done"). The
 * plan and the team never draw in the body; their detail is in "How this answer was made" once the answer is saved.
 */
export function LiveLine({ live, streaming, progress, specialistName }: {
  live: LiveTurn;
  streaming: boolean;
  /** The turn's progress blocks (the plan, the team). */
  progress: ReadonlyArray<Record<string, unknown>>;
  specialistName: (key: string) => string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const toolLabel = useToolLabel();
  if (!streaming || live.error || live.content.trim()) return null;
  const plan = progress.find((b) => b.type === "plan");
  const steps = Array.isArray(plan?.steps) ? (plan!.steps as ProgressStep[]) : [];
  const team = progress.find((b) => b.type === "team");
  const members = Array.isArray(team?.members) ? (team!.members as ProgressMember[]) : [];
  const running = steps.find((s) => s.status === "running");
  const call = [...live.toolCalls].reverse().find((c) => c.phase === "start");
  const stage = live.stage ? t(`ai.chat.stage.${live.stage}`, { defaultValue: t("ai.chat.stage.thinking") }) : t("ai.chat.stage.thinking");
  const label = running?.label ? (locale === "ar" && running.labelAr ? running.labelAr : running.label)
    : call ? (call.tool === "delegate_to_agent" && call.agentKey ? t("ai.chat.askedSpecialist", { agent: specialistName(call.agentKey) }) : toolLabel(call.tool, true))
    : stage;
  const of = steps.length > 1 ? { n: steps.filter((s) => s.status === "done").length, m: steps.length }
    : members.length > 1 ? { n: members.filter((s) => s.status === "done").length, m: members.length }
    : null;
  return (
    <p className="ea-live-line" role="status" aria-live="polite">
      <Shimmer>{label}</Shimmer>
      {of ? <span className="of">{t("assistant:econic.agent.w.planProgress", { ...of, defaultValue: "{{n}} of {{m}} done" })}</span> : null}
    </p>
  );
}

/** Model, response time, tokens and the raw tool trace: for the console's test bench only. */
export function DebugDetails({ modelId, latencyMs, inputTokens, outputTokens, calls }: {
  modelId?: string | null; latencyMs?: number | null; inputTokens?: number | null; outputTokens?: number | null; calls: TraceCall[];
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (!modelId && calls.length === 0) return null;
  return (
    <Collapsible className="mt-1">
      <CollapsibleTrigger className="group/details inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-sand-600 transition-colors hover:bg-sand-100 hover:text-aeblack-900">
        <Info className="size-3" /> {t("ai.chat.details")}
        <ChevronDown className="size-3 transition-transform group-data-[state=open]/details:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1.5 rounded-lg border border-sand-200 bg-sand-50/70 p-2.5 text-[11px] text-sand-800" dir="ltr">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            {latencyMs ? <><dt className="text-sand-600">{t("ai.chat.detailsLatency")}</dt><dd className="font-mono tabular">{formatNumber(latencyMs, locale)} ms</dd></> : null}
            {inputTokens || outputTokens ? <><dt className="text-sand-600">{t("ai.chat.detailsTokens")}</dt><dd className="font-mono tabular">{formatNumber(inputTokens ?? 0, locale)} / {formatNumber(outputTokens ?? 0, locale)}</dd></> : null}
          </dl>
          {calls.length > 0 ? (
            <>
              <p className="mt-2 text-sand-600">{t("ai.chat.detailsTools")}</p>
              <ul className="mt-0.5 space-y-0.5 font-mono">
                {calls.map((c) => (
                  <li key={c.id} className="min-w-0">
                    <span className={c.phase === "error" ? "text-danger-700" : undefined}>{c.tool}</span>
                    {c.agentKey ? <span className="text-sand-600"> → {c.agentKey}</span> : null}
                    {c.durationMs ? <span className="text-sand-600"> · {c.durationMs} ms</span> : null}
                    {c.summary ? <span className="block truncate text-sand-600" title={c.summary}>{c.summary}</span> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
